// packages/web/src/components/Preloader.tsx
import React, { useState, useEffect } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { useSiteConfig } from '../context/SiteConfigContext'

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  x: 12 + ((i * 37) % 76),
  y: 8 + ((i * 53) % 84),
  size: 2 + (i % 3),
  delay: (i % 6) * 0.08,
}))

export const Preloader: React.FC = () => {
  const { config } = useSiteConfig()
  const [visible, setVisible] = useState(true)
  const [phase, setPhase] = useState<'glow' | 'reveal' | 'exit'>('glow')
  const reduceMotion = useReducedMotion()

  const brandInitial = config.brand_name?.charAt(0)?.toUpperCase() || 'B'
  const brandName = config.brand_name || "Buddy's Choice"
  const tagline = config.brand_tagline || 'Fine Jewellery & Accessories'

  useEffect(() => {
    if (reduceMotion) {
      const t = setTimeout(() => setVisible(false), 500)
      return () => clearTimeout(t)
    }

    const timers = [
      setTimeout(() => setPhase('reveal'), 500),
      setTimeout(() => setPhase('exit'), 2200),
      setTimeout(() => setVisible(false), 2800),
    ]
    return () => timers.forEach(clearTimeout)
  }, [reduceMotion])

  if (!visible) return null

  if (reduceMotion) {
    return (
      <div className="fixed inset-0 z-[9999] bg-[var(--bg-main)] flex items-center justify-center">
        <span className="font-title text-xl gold-shimmer-text tracking-widest uppercase">{brandName}</span>
      </div>
    )
  }

  const showReveal = phase !== 'glow'
  const isExiting = phase === 'exit'

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-[var(--bg-main)]"
      initial={{ opacity: 1 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Radial bloom */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: showReveal ? 1 : 0.35 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            'radial-gradient(ellipse 55% 45% at 50% 48%, color-mix(in srgb, var(--gold-main) 22%, transparent), transparent 72%)',
        }}
      />

      {/* Gold dust */}
      {PARTICLES.map(({ id, x, y, size, delay }) => (
        <motion.span
          key={id}
          className="absolute rounded-full bg-[var(--gold-light)] pointer-events-none"
          style={{ left: `${x}%`, top: `${y}%`, width: size, height: size }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: showReveal ? [0.15, 0.7, 0.2] : 0,
            scale: showReveal ? [0, 1.2, 0.8] : 0,
            y: showReveal ? [0, -18, -36] : 0,
          }}
          transition={{
            duration: 2.2,
            delay,
            ease: 'easeOut',
          }}
        />
      ))}

      {/* Sweeping light arc */}
      <motion.div
        className="absolute w-[min(90vw,520px)] h-[min(90vw,520px)] rounded-full border border-[var(--gold-main)]/20 pointer-events-none"
        initial={{ scale: 0.4, opacity: 0, rotate: 0 }}
        animate={{
          scale: showReveal ? 1.15 : 0.55,
          opacity: showReveal ? 0.55 : 0,
          rotate: showReveal ? 90 : 0,
        }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.div
        className="absolute w-[min(70vw,400px)] h-[min(70vw,400px)] rounded-full border border-dashed border-[var(--gold-main)]/15 pointer-events-none"
        initial={{ scale: 0.3, opacity: 0, rotate: 0 }}
        animate={{
          scale: showReveal ? 1 : 0.4,
          opacity: showReveal ? 0.35 : 0,
          rotate: showReveal ? -120 : 0,
        }}
        transition={{ duration: 1.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      />

      <div className="relative flex flex-col items-center text-center px-6">
        {/* Monogram */}
        <motion.div
          className="relative mb-8"
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{
            scale: showReveal ? 1 : 0.75,
            opacity: showReveal ? 1 : 0,
          }}
          transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div
            className="absolute -inset-3 rounded-full bg-[var(--gold-main)]/20 blur-xl"
            animate={{ opacity: showReveal ? [0.3, 0.7, 0.4] : 0 }}
            transition={{ duration: 1.8, repeat: showReveal ? Infinity : 0, repeatType: 'reverse' }}
          />
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-tr from-[var(--gold-deep)] via-[var(--gold-main)] to-[var(--gold-light)] p-[2px] shadow-[var(--shadow-gold-glow)]">
            <div className="w-full h-full rounded-full bg-[var(--bg-main)] flex items-center justify-center overflow-hidden">
              <motion.span
                className="font-serif font-black text-4xl sm:text-5xl gold-shimmer-text"
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: showReveal ? 0 : 12, opacity: showReveal ? 1 : 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                {brandInitial}
              </motion.span>
            </div>
          </div>

          {/* Diamond accents */}
          {[0, 90, 180, 270].map((deg, i) => (
            <motion.span
              key={deg}
              className="absolute w-1.5 h-1.5 bg-[var(--gold-light)] rotate-45"
              style={{
                top: '50%',
                left: '50%',
                marginTop: -3,
                marginLeft: -3,
                transformOrigin: '3px 52px',
                transform: `rotate(${deg}deg) translateY(-52px)`,
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: showReveal ? 1 : 0, scale: showReveal ? 1 : 0 }}
              transition={{ duration: 0.4, delay: 0.25 + i * 0.06 }}
            />
          ))}
        </motion.div>

        {/* Brand text */}
        <motion.div
          className="space-y-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: showReveal ? 1 : 0,
            y: showReveal ? 0 : 20,
          }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className="text-lg sm:text-2xl font-title font-bold tracking-[0.22em] uppercase gold-shimmer-text">
            {brandName}
          </h1>
          <p className="text-[10px] sm:text-[11px] text-[var(--gold-main)] uppercase tracking-[0.32em] font-sans">
            {tagline}
          </p>
        </motion.div>

        {/* Loading line */}
        <motion.div
          className="mt-8 w-36 h-px bg-[var(--border)] overflow-hidden rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: showReveal ? 1 : 0 }}
          transition={{ delay: 0.35 }}
        >
          <motion.div
            className="h-full bg-gradient-to-r from-transparent via-[var(--gold-main)] to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: showReveal ? '100%' : '-100%' }}
            transition={{ duration: 1.1, ease: 'easeInOut', repeat: showReveal ? 1 : 0 }}
          />
        </motion.div>
      </div>

      {/* Exit curtain */}
      <motion.div
        className="absolute inset-x-0 top-0 h-1/2 bg-[var(--bg-main)] pointer-events-none"
        initial={{ y: '-100%' }}
        animate={{ y: isExiting ? 0 : '-100%' }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      />
      <motion.div
        className="absolute inset-x-0 bottom-0 h-1/2 bg-[var(--bg-main)] pointer-events-none"
        initial={{ y: '100%' }}
        animate={{ y: isExiting ? 0 : '100%' }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      />
    </motion.div>
  )
}
